import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDateTime, newIdempotencyKey, scr } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { sealBidPayload } from '../../../lib/crypto';
import { M11_ROLES, hasAnyRole } from '../../../lib/roles';
import type { Bid, BidSubmitResponse, SupplierProfile, TenderLot, TenderNotice } from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { ErrorNote, Field, InfoNote, Spinner, StatusBadge } from '../../../ui/components';

const BIDDABLE = ['PUBLISHED', 'CLARIFICATIONS'];

export function BidTab({ tender, onChanged }: { tender: TenderNotice; onChanged: () => void }) {
  const { persona } = useAuth();
  const isStaff = hasAnyRole(
    persona?.roles,
    M11_ROLES.PROCUREMENT_OFFICER,
    M11_ROLES.EVALUATOR,
    M11_ROLES.TENDER_BOARD,
    M11_ROLES.NPTB_OVERSIGHT,
  );

  if (isStaff) return <StaffBidList tender={tender} />;
  return <VendorBidPanel tender={tender} onChanged={onChanged} />;
}

function StaffBidList({ tender }: { tender: TenderNotice }) {
  const bidsQ = useApiQuery<{ items: Bid[] }>(`/api/v1/m11/tenders/${tender.id}/bids`);
  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Sealed bids ({bidsQ.data?.items.length ?? 0})</h2>
      <p className="mb-3 text-xs text-slate-500">
        Ciphertext only until a 2-of-3 evaluator quorum unseals the tender (Evaluation tab) — no one, not even staff, can read a bid before then.
      </p>
      {bidsQ.error ? <ErrorNote message={bidsQ.error} /> : null}
      {bidsQ.loading ? (
        <Spinner />
      ) : (bidsQ.data?.items.length ?? 0) === 0 ? (
        <p className="text-xs text-slate-400">No bids submitted yet.</p>
      ) : (
        <ul className="space-y-2">
          {bidsQ.data!.items.map((b) => (
            <li key={b.id} className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-slate-500">supplier {b.supplierId}{b.lotId ? ` · lot ${b.lotId}` : ''}</span>
                <StatusBadge status={b.openedAt ? 'ISSUED' : 'PENDING'} />
              </div>
              <div className="mt-1 text-[11px] text-slate-400">Submitted {formatDateTime(b.submittedAt)} · sha256:{b.payloadSha256.slice(0, 16)}…</div>
              {b.decryptedPayload ? (
                <div className="mt-2 rounded bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                  <div className="font-semibold">Price: {scr((b.decryptedPayload as { priceScr?: number }).priceScr ?? null)}</div>
                  {typeof (b.decryptedPayload as { proposal?: string }).proposal === 'string' ? (
                    <div className="mt-1 whitespace-pre-wrap">{(b.decryptedPayload as { proposal: string }).proposal}</div>
                  ) : null}
                </div>
              ) : (
                <div className="mt-1 text-xs text-slate-400">Sealed — not yet opened.</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VendorBidPanel({ tender, onChanged }: { tender: TenderNotice; onChanged: () => void }) {
  const { persona } = useAuth();
  const actingFor = persona?.actingForBusinessId;

  // Hooks are called unconditionally (never after an early return) even
  // though this panel only makes sense once signed in with a business
  // mandate — the queries themselves take a null path until then. M11's
  // supplierEntityId() (packages/modules/m11-egp/src/routes/shared.ts)
  // requires X-Acting-For outright for these three, so it's threaded
  // through explicitly; lotsQ needs no mandate.
  const supplierQ = useApiQuery<{ items: SupplierProfile[] }>(actingFor ? '/api/v1/m11/suppliers/me' : null, actingFor);
  const invitedQ = useApiQuery<{ tenderId: string; invited: boolean }>(actingFor ? `/api/v1/m11/tenders/${tender.id}/invitations/me` : null, actingFor);
  const myBidsQ = useApiQuery<{ items: Bid[] }>(actingFor ? `/api/v1/m11/tenders/${tender.id}/bids/me` : null, actingFor);
  const lotsQ = useApiQuery<{ items: TenderLot[] }>(`/api/v1/m11/tenders/${tender.id}/lots`);

  if (!persona) {
    return <InfoNote message="Sign in as a business representative to bid on this tender — see the sidebar's Sign in link." />;
  }
  if (!actingFor) {
    return (
      <InfoNote message="Bidding requires acting for a registered business (X-Acting-For mandate) — M11 suppliers are always entities, never individuals. Sign in as a business representative persona to bid." />
    );
  }

  if (supplierQ.loading || invitedQ.loading) return <Spinner label="Checking your supplier standing…" />;

  const supplier = supplierQ.data?.items.find((s) => s.status !== 'REJECTED');
  const verified = supplier?.status === 'VERIFIED';
  const eligible = tender.method === 'OPEN' || invitedQ.data?.invited;

  return (
    <div className="space-y-5">
      {!supplier ? (
        <InfoNote message={
          <>
            No supplier profile yet for this business. <Link to="/procurement/suppliers" className="font-semibold underline">Register as a supplier</Link> before bidding.
          </>
        } />
      ) : !verified ? (
        <InfoNote message={`Your supplier profile (${supplier.refNo}) is ${supplier.status} — bidding requires a VERIFIED profile.`} />
      ) : supplier.blacklist.status !== 'NONE' ? (
        <ErrorNote message={`This supplier is currently ${supplier.blacklist.status} and cannot bid.`} />
      ) : !eligible ? (
        <InfoNote message={`This is a ${tender.method} tender — bidding by invitation only, and this business has not been invited.`} />
      ) : !BIDDABLE.includes(tender.status) ? (
        <InfoNote message={`Bidding is not open — tender is ${tender.status}.`} />
      ) : (
        <SealAndSubmit tender={tender} lots={lotsQ.data?.items ?? []} actingFor={actingFor} onSubmitted={() => { myBidsQ.reload(); onChanged(); }} />
      )}

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Your bids on this tender</h2>
        {myBidsQ.error ? <ErrorNote message={myBidsQ.error} /> : null}
        {myBidsQ.loading ? <Spinner /> : (myBidsQ.data?.items.length ?? 0) === 0 ? (
          <p className="text-xs text-slate-400">No bid submitted yet.</p>
        ) : (
          <ul className="space-y-2">
            {myBidsQ.data!.items.map((b) => (
              <li key={b.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span>{b.lotId ? `Lot ${b.lotId}` : 'Single lot'}</span>
                  <StatusBadge status={b.openedAt ? 'ISSUED' : 'PENDING'} />
                </div>
                <div className="mt-1 text-[11px] text-slate-400">Submitted {formatDateTime(b.submittedAt)}</div>
                <div className="mt-1 font-mono text-[11px] text-slate-500">receipt:{b.receiptHash.slice(0, 24)}…</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SealAndSubmit({ tender, lots, actingFor, onSubmitted }: { tender: TenderNotice; lots: TenderLot[]; actingFor: string; onSubmitted: () => void }) {
  const [lotId, setLotId] = useState('');
  const [priceScr, setPriceScr] = useState('');
  const [proposal, setProposal] = useState('');
  const [validityDays, setValidityDays] = useState('90');
  const [step, setStep] = useState<'idle' | 'sealing' | 'submitting' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BidSubmitResponse | null>(null);

  const submit = async () => {
    setError(null);
    setStep('sealing');
    try {
      const keyRes = await api.get<{ tenderId: string; publicKeyPem: string }>(`/api/v1/m11/tenders/${tender.id}/seal-public-key`);
      const payload = {
        priceScr: Number.parseFloat(priceScr),
        proposal,
        validityDays: Number.parseInt(validityDays, 10),
      };
      const envelope = await sealBidPayload(keyRes.publicKeyPem, payload);
      setStep('submitting');
      const res = await api.post<BidSubmitResponse>(
        `/api/v1/m11/tenders/${tender.id}/bids`,
        { lotId: lotId || undefined, ...envelope, documents: [] },
        { actingFor, idempotencyKey: newIdempotencyKey() },
      );
      setResult(res);
      setStep('done');
      onSubmitted();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `${e.message} (${e.code})`
          : e instanceof Error
            ? `Sealing failed in-browser: ${e.message}`
            : 'Bid submission failed',
      );
      setStep('idle');
    }
  };

  if (step === 'done' && result) {
    return (
      <div className="card border-emerald-200 bg-emerald-50 p-5">
        <h2 className="text-sm font-bold text-emerald-800">Bid sealed and submitted</h2>
        <p className="mt-1 text-xs text-emerald-700">
          Your bid content was encrypted in your browser (AES-256-GCM, RSA-OAEP-wrapped) before it ever left the device —
          the server only ever stored ciphertext.
        </p>
        <dl className="mt-3 space-y-1 font-mono text-[11px] text-emerald-800">
          <div>receiptHash: {result.receiptHash}</div>
          <div>payloadSha256: {result.payloadSha256}</div>
        </dl>
      </div>
    );
  }

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Submit a sealed bid</h2>
      <p className="mb-3 text-xs text-slate-500">
        Encrypted client-side against this tender's public seal key — nobody can read it, not even this portal, until the
        evaluation committee's quorum unseal ceremony after bidding closes.
      </p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      {tender.bidBondScr && Number(tender.bidBondScr) > 0 ? (
        <div className="mb-3">
          <BidBondNote tender={tender} actingFor={actingFor} />
        </div>
      ) : null}
      <div className="space-y-3">
        {lots.length > 0 ? (
          <Field label="Lot">
            <select className="field-input" value={lotId} onChange={(e) => setLotId(e.target.value)}>
              <option value="">Choose a lot…</option>
              {lots.map((l) => (
                <option key={l.id} value={l.id}>Lot {l.lotNo} — {l.title}</option>
              ))}
            </select>
          </Field>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Bid price (SCR)"><input className="field-input" type="number" min="0" step="0.01" value={priceScr} onChange={(e) => setPriceScr(e.target.value)} /></Field>
          <Field label="Validity (days)"><input className="field-input" type="number" min="1" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} /></Field>
        </div>
        <Field label="Technical proposal summary">
          <textarea className="field-input" rows={4} value={proposal} onChange={(e) => setProposal(e.target.value)} placeholder="Methodology, timeline, key personnel…" />
        </Field>
        <button
          className="btn-primary w-full"
          onClick={submit}
          disabled={step !== 'idle' || !priceScr || proposal.length < 5 || (lots.length > 0 && !lotId)}
        >
          {step === 'sealing' ? 'Sealing in browser…' : step === 'submitting' ? 'Submitting…' : 'Seal & submit bid'}
        </button>
      </div>
    </div>
  );
}

function BidBondNote({ tender, actingFor }: { tender: TenderNotice; actingFor: string }) {
  const [error, setError] = useState<string | null>(null);
  const [intent, setIntent] = useState<{ payIntentRefNo: string; status: string; total: number; currency: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const raise = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ payIntentRefNo: string; status: string; total: number; currency: string }>(
        `/api/v1/m11/tenders/${tender.id}/bid-bond-intent`,
        undefined,
        { actingFor },
      );
      setIntent(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not raise the bid-bond payment intent');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
      <div className="font-semibold">A {scr(tender.bidBondScr)} bid bond is required before this bid can be accepted.</div>
      {error ? <div className="mt-1"><ErrorNote message={error} /></div> : null}
      {intent ? (
        <div className="mt-1">
          SeyPay intent {intent.payIntentRefNo} raised ({intent.status}) — pay via SeyPay, then the bond clears automatically
          once its webhook confirms payment (service-to-service; there is no manual "mark paid" step this portal can trigger).
        </div>
      ) : (
        <button className="btn-secondary mt-1 !border-amber-300 !bg-white !py-1 text-xs" onClick={raise} disabled={busy}>
          {busy ? 'Raising…' : 'Raise bid-bond payment intent'}
        </button>
      )}
    </div>
  );
}
